import { translateLabel } from "@/i18n/messages";
import React, { useRef, useState } from "react";
import {
  SparkleIcon,
  SpinnerGapIcon,
} from "@phosphor-icons/react";
import { api } from "@/lib/apiClient";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { trackEvent } from "@/lib/analytics";

const EXAMPLES = [
  "Lift access within 50 km of Würzburg, at least 5 trails",
  "Flow trails within 150 km of München",
  "Family parks within 100 km of Nürnberg",
];

const humanize = (value) => String(value || "")
  .toLowerCase()
  .split("_")
  .filter(Boolean)
  .map((part) => part[0].toUpperCase() + part.slice(1))
  .join(" ");

const criteriaChips = (result) => {
  const criteria = result?.criteria || {};
  return [
    criteria.origin?.label ? `From ${translateLabel(criteria.origin.label)}` : null,
    criteria.radius_km != null ? `Within ${criteria.radius_km} km` : null,
    criteria.type && criteria.type !== "everything" ? humanize(criteria.type) : null,
    ...(criteria.access || []).map((value) => humanize(value)),
    ...(criteria.styles || []).map((value) => humanize(value)),
    ...(criteria.difficulty || []).map((value) => humanize(value)),
    criteria.min_trails != null ? `At least ${criteria.min_trails} trails` : null,
    criteria.min_rating != null ? `Rating ${criteria.min_rating}+` : null,
    criteria.open_now === true ? "Open now" : null,
  ].filter(Boolean);
};

const preferenceChips = (result) => {
  const criteria = result?.preferences || {};
  return [
    criteria.type ? humanize(criteria.type) : null,
    ...(criteria.access || []).map((value) => humanize(value)),
    ...(criteria.styles || []).map((value) => humanize(value)),
    ...(criteria.difficulty || []).map((value) => humanize(value)),
    criteria.min_trails != null ? `At least ${criteria.min_trails} trails` : null,
    criteria.min_rating != null ? `Rating ${criteria.min_rating}+` : null,
    criteria.open_now === true ? "Open now" : null,
  ].filter(Boolean);
};

const timezone = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "Europe/Berlin";
  } catch {
    return "Europe/Berlin";
  }
};

export function RideAssistant({ currentOrigin, currentCriteria, onCriteria, onResults, onGroupDiscovery }) {
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [advancedLoading, setAdvancedLoading] = useState(false);
  const [interpretation, setInterpretation] = useState(null);
  const [advancedResult, setAdvancedResult] = useState(null);
  const [error, setError] = useState("");
  const requestId = useRef(0);
  const lastSubmittedMessage = useRef("");

  const currentOriginInput = () => currentOrigin
    ? {
      label: currentOrigin.name || translateLabel("Current search area"),
      lat: currentOrigin.lat,
      lng: currentOrigin.lng,
    }
    : null;

  const handleOpenChange = (nextOpen) => {
    if (!nextOpen) {
      requestId.current += 1;
      setLoading(false);
      setAdvancedLoading(false);
    }
    setOpen(nextOpen);
  };

  const submit = async (event) => {
    event?.preventDefault();
    const text = message.trim();
    if (!text || loading || advancedLoading) return;

    const activeRequest = ++requestId.current;
    lastSubmittedMessage.current = text;
    setLoading(true);
    setError("");
    setInterpretation(null);
    setAdvancedResult(null);

    try {
      const { data } = await api.post("/discovery/interpret", {
        message: text,
        current_origin: currentOriginInput(),
        current_criteria: currentCriteria || null,
        timezone: timezone(),
      });
      if (activeRequest !== requestId.current) return;

      setInterpretation(data);
      trackEvent("ride_assistant_interpretation", {
        status: data.status || "UNKNOWN",
        criteria_count: criteriaChips(data).length,
        advanced_criteria_count: data.advanced_criteria?.length || 0,
      });

      if (data.status === "READY") {
        onCriteria?.(data);
        setOpen(false);
      } else if (
        data.status === "ADVANCED"
        && (data.intent?.origins?.length || 0) <= 1
      ) {
        setAdvancedLoading(true);
        try {
          const advancedResponse = await api.post("/ride-assistant", {
            message: text,
            current_origin: currentOriginInput(),
            timezone: timezone(),
          });
          if (activeRequest !== requestId.current) return;
          const advancedData = advancedResponse.data;
          setAdvancedResult(advancedData);
          trackEvent("ride_assistant_search", {
            source: "advanced_auto",
            result_count: advancedData.recommendations?.length ?? 0,
            routing_available: Boolean(advancedData.routing_available),
            match_kind: advancedData.match_kind || "UNKNOWN",
          });
          if (advancedData.recommendations?.length) {
            onResults?.(advancedData);
            setOpen(false);
          }
        } finally {
          if (activeRequest === requestId.current) setAdvancedLoading(false);
        }
      }
    } catch (requestError) {
      if (activeRequest !== requestId.current) return;
      setError(requestError.response?.data?.detail || translateLabel("Could not process that request."));
    } finally {
      if (activeRequest === requestId.current) setLoading(false);
    }
  };

  const runAdvancedSearch = async () => {
    const text = lastSubmittedMessage.current || message.trim();
    if (!text || advancedLoading || loading) return;

    const activeRequest = ++requestId.current;
    setAdvancedLoading(true);
    setError("");
    setAdvancedResult(null);

    try {
      const { data } = await api.post("/ride-assistant", {
        message: text,
        current_origin: currentOriginInput(),
        timezone: timezone(),
      });
      if (activeRequest !== requestId.current) return;

      setAdvancedResult(data);
      trackEvent("ride_assistant_search", {
        source: "advanced_legacy",
        result_count: data.recommendations?.length ?? 0,
        routing_available: Boolean(data.routing_available),
        match_kind: data.match_kind || "UNKNOWN",
      });

      if (data.recommendations?.length) {
        onResults?.(data);
        setOpen(false);
      }
    } catch (requestError) {
      if (activeRequest !== requestId.current) return;
      setError(requestError.response?.data?.detail || translateLabel("The deeper search could not run."));
    } finally {
      if (activeRequest === requestId.current) setAdvancedLoading(false);
    }
  };

  const prepareRelaxation = (option) => {
    const base = lastSubmittedMessage.current || message.trim();
    setMessage(`${base}. ${option}.`);
    setAdvancedResult(null);
    setError("");
  };

  const appliedChips = criteriaChips(interpretation);
  const optionalChips = preferenceChips(interpretation);
  const canHandoffToGroup = interpretation?.status === "ADVANCED"
    && (interpretation?.intent?.origins?.length || 0) > 1
    && typeof onGroupDiscovery === "function";

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <button
          type="button"
          aria-label={translateLabel("Open ride assistant")}
          title={translateLabel("Ride assistant")}
          data-testid="ride-assistant-trigger"
          className={`absolute right-4 z-[1260] flex h-12 w-12 items-center justify-center rounded-full border border-[#CCFF00]/40 bg-[#111113] text-[#CCFF00] shadow-2xl transition-transform hover:scale-105 active:scale-95 ${currentOrigin ? "bottom-36 md:bottom-36" : "bottom-28 md:bottom-28"}`}
        >
          <SparkleIcon size={22} weight="fill" />
        </button>
      </DialogTrigger>

      <DialogContent className="z-[1450] w-[calc(100%-1.5rem)] max-w-xl overflow-hidden rounded-3xl border-white/10 bg-[#121214] p-5 text-white sm:p-6">
        <DialogHeader className="pb-1">
          <div className="flex items-center gap-2 text-[#CCFF00]">
            <SparkleIcon size={18} weight="fill" />
            <span className="text-[10px] font-extrabold uppercase tracking-[0.18em]">{translateLabel("Ride assistant")}</span>
          </div>
          <DialogTitle className="font-display text-2xl uppercase text-white">{translateLabel("Where should I ride?")}</DialogTitle>
          <p className="text-sm leading-5 text-zinc-400">
            {translateLabel("Describe what you want. Supported criteria are applied to the same filters, list and map you already use.")}
          </p>
        </DialogHeader>

        <div className="max-h-[68dvh] overflow-y-auto overscroll-contain pr-1">
          <form onSubmit={submit} className="mt-3">
            <textarea
              value={message}
              onChange={(event) => setMessage(event.target.value)}
              rows={4}
              maxLength={700}
              autoFocus
              placeholder={translateLabel("e.g. Lift and flow within 150 km of Würzburg, at least 5 trails")}
              data-testid="ride-assistant-input"
              className="w-full resize-none rounded-2xl border border-white/10 bg-black/30 px-4 py-3 text-sm leading-6 text-white outline-none placeholder:text-zinc-600 focus:border-[#CCFF00]/50"
            />

            <div className="mt-2 flex flex-wrap gap-1.5">
              {EXAMPLES.map((example) => (
                <button
                  key={example}
                  type="button"
                  onClick={() => setMessage(example)}
                  className="rounded-full border border-white/10 bg-white/[0.03] px-2.5 py-1.5 text-[11px] font-semibold text-zinc-400 transition-colors hover:bg-white/[0.07] hover:text-white"
                >
                  {translateLabel(example)}
                </button>
              ))}
            </div>

            <button
              type="submit"
              disabled={loading || advancedLoading || !message.trim()}
              className="mt-3 flex w-full items-center justify-center gap-2 rounded-full bg-[#CCFF00] py-3 text-sm font-extrabold text-black transition-opacity disabled:cursor-not-allowed disabled:opacity-40"
              data-testid="ride-assistant-submit"
            >
              {loading ? <SpinnerGapIcon size={17} className="animate-spin" /> : <SparkleIcon size={16} weight="fill" />}
              {translateLabel(loading ? "Reading criteria…" : "Apply ride criteria")}
            </button>
          </form>

          {error && (
            <div className="mt-4 rounded-2xl border border-red-400/20 bg-red-400/[0.06] px-4 py-3 text-sm text-red-200">
              {error}
            </div>
          )}

          {interpretation && interpretation.status !== "READY" && (
            <div className="mt-5 rounded-2xl border border-white/10 bg-white/[0.025] px-4 py-3" data-testid="ai-interpretation-review">
              <div className="text-[10px] font-extrabold uppercase tracking-[0.16em] text-[#CCFF00]">
                Interpreted request
              </div>

              {appliedChips.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {appliedChips.map((chip) => (
                    <span
                      key={chip}
                      className="rounded-full border border-white/[0.08] bg-black/20 px-2.5 py-1 text-[10px] font-bold text-zinc-300"
                    >
                      {chip}
                    </span>
                  ))}
                </div>
              )}

              {optionalChips.length > 0 && (
                <p className="mt-3 text-xs leading-5 text-zinc-400">
                  Optional preferences are visible but are not applied as strict filters yet: {optionalChips.join(" · ")}
                </p>
              )}

              {interpretation.clarifications?.length > 0 && (
                <div className="mt-3 text-xs leading-5 text-amber-200">
                  {interpretation.clarifications.map((item) => <p key={item}>{item}</p>)}
                </div>
              )}

              {interpretation.origin_options?.length > 1 && (
                <p className="mt-2 text-xs leading-5 text-zinc-400">
                  Location choices: {interpretation.origin_options.map((item) => item.label).join(" · ")}
                </p>
              )}

              {interpretation.unsupported_criteria?.length > 0 && (
                <p className="mt-3 text-xs leading-5 text-amber-200">
                  Not supported by ordinary discovery yet: {interpretation.unsupported_criteria.join(" · ")}
                </p>
              )}

              {interpretation.status === "ADVANCED" && (
                <div className="mt-4 rounded-xl border border-amber-300/20 bg-amber-300/[0.06] p-3" data-testid="advanced-ai-path">
                  <div className="text-[10px] font-extrabold uppercase tracking-[0.14em] text-amber-200">
                    {canHandoffToGroup ? "Group Discovery" : "Some criteria need a deeper search"}
                  </div>
                  <p className="mt-1.5 text-xs leading-5 text-zinc-300">
                    {canHandoffToGroup
                      ? "This request has multiple starting locations. Continue in Group Discovery so every rider's drive limit and route can be checked explicitly."
                      : `${interpretation.advanced_criteria?.join(" · ")} need route, weather or trail-level evaluation beyond the standard filters.`}
                  </p>
                  <button
                    type="button"
                    onClick={() => {
                      if (canHandoffToGroup) {
                        onGroupDiscovery(interpretation);
                        setOpen(false);
                        return;
                      }
                      runAdvancedSearch();
                    }}
                    disabled={!canHandoffToGroup && advancedLoading}
                    className="mt-3 flex w-full items-center justify-center gap-2 rounded-full border border-amber-200/30 bg-amber-200/10 py-2.5 text-xs font-extrabold text-amber-100 transition-colors hover:bg-amber-200/15 disabled:opacity-40"
                    data-testid={canHandoffToGroup ? "continue-group-discovery" : "run-advanced-ai-search"}
                  >
                    {!canHandoffToGroup && advancedLoading && <SpinnerGapIcon size={15} className="animate-spin" />}
                    {canHandoffToGroup
                      ? "Continue in Group Discovery"
                      : advancedLoading
                        ? "Searching…"
                        : "Run deeper search"}
                  </button>
                </div>
              )}
            </div>
          )}

          {advancedResult && !advancedResult.recommendations?.length && (
            <div className="mt-4 rounded-2xl border border-white/10 bg-white/[0.025] px-4 py-3">
              <div className="text-[10px] font-extrabold uppercase tracking-[0.16em] text-[#CCFF00]">
                No verified advanced matches
              </div>
              <p className="mt-2 text-xs leading-5 text-zinc-400">
                {advancedResult.evaluation_complete
                  ? "No verified destination matched the requested criteria. Nothing was relaxed automatically."
                  : "No verified match was found in the evaluated candidates. The search was bounded, so this is not an exhaustive no-match claim."}
              </p>
              {advancedResult.unverified_reasons?.map((reason) => (
                <p key={reason} className="mt-2 text-xs font-semibold text-amber-300">{reason}</p>
              ))}
              {advancedResult.relaxation_options?.length > 0 && (
                <div className="mt-3">
                  <p className="text-[10px] font-extrabold uppercase tracking-[0.14em] text-zinc-500">
                    Optional relaxation
                  </p>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {advancedResult.relaxation_options.map((option) => (
                      <button
                        key={option}
                        type="button"
                        onClick={() => prepareRelaxation(option)}
                        className="rounded-full border border-white/10 bg-white/[0.04] px-3 py-1.5 text-[11px] font-bold text-zinc-300 hover:bg-white/[0.08]"
                      >
                        {option}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
